import _ from "lodash";
import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classNames from "classnames";

import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import RubricTable from "Rubric/components/RubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import MYPObjectiveRubric from "UnitPlans/components/IBcomponents/AssessmentTool/MYPObjectiveRubric";
import AIAnalysisScale from "UnitPlans/components/IBcomponents/AIAnalysisScale";
import CriteriaRubricAssessmentWrapper from "UnitPlans/components/IBcomponents/CriteriaRubricAssessmentWrapper";
import AssessmentIBScaleTool from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool";
import AddLearningGoals from "UnitPlans/components/IBcomponents/AddLearningGoals";

import {
  getAssessmentToolsDetailsByStudentAssignmentIdQuery,
  getAssessmentToolsDetailsByPostIdQuery,
} from "modules/CommonQuery";
import { getAssessmentToolsByQuestionSubmissionIdQuery } from "Worksheet/modules/WorksheetQueries";
import { getAssessmentToolsByQuestionSubmissionIdFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  getAssessmentToolsDetailsByStudentAssignmentIdFromCache,
  getAssessmentToolsDetailsByPostIdFromCache,
} from "modules/CommonGraphqlHelpers";

import { getMYPObjectiveRubricOptions } from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { handleAssessmentToolResponseInCache } from "AssessmentEvaluation/modules/AssessmentEvaluationModule";

import withLoader from "UIComponents/GqlWithLoader";
import withQueryParamsBasedInterface from "UIComponents/QueryParamsBasedInterfaceHOC";
import { fontStyles } from "@toddle-edu/ds-theme";

import {
  getAssessmentToolText,
  getScoreValueElementRatings,
  getAssessmentToolSelfEvaluationModeStyles,
  getAssessmentToolRevampedViewModeStyles,
} from "ClassRoom/modules/utils";
import { getUserType } from "modules/CommonSelectors";
import { useI18n } from "Hooks";
import {
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import { QUESTION_SUBMISSION } from "Worksheet/modules/WorksheetConstants";
import { ASSESSMENT_TOOL_ICON_MAPPING } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/Constants";
import StandardRubric from "AppComponents/StandardRubric";
import AssessmentGradeScaleTool from "AppComponents/AssessmentGradeScaleTool";
import classes from "./ViewerComponent.scss";

const styles = {
  toolStyles: {
    leftContainerStyle: {
      fontSize: "1.6rem",
      ...fontStyles.bold,
      color: "var(--text-primary)",
    },
    criteriaRatingStyle: {
      height: "36px",
    },
  },
  evaluationContainerStyle: {
    marginBottom: "16px",
  },
  evaluationBodyStyle: {
    padding: "0px",
    borderRadius: "8px",
  },
  criteriaSetHeaderRowStyle: {
    backgroundColor: "var(--violet-950)",
    padding: "0px 12px",
    height: "44px",
  },
  leftContainerStyle: {
    left: "12px",
  },
  headerRowLeftContainerStyle: {
    backgroundColor: "var(--violet-950)",
  },
  customHeaderLabelStyle: {
    fontSize: "1.4rem",
    ...fontStyles.demiBold,
    color: "var(--text-primary)",
  },
  criteriaSetHeaderLabelPreviewStyle: {
    fontSize: "1.4rem",
    ...fontStyles.demiBold,
    color: "var(--text-primary)",
  },
  headerIconStyle: {
    color: "var(--icon-secondary)",
  },
};

const AssessmentToolWrapper = ({
  showHeader = true,
  headerClasses,
  toolIcon,
  showToolIcon = false,
  assessmentToolText,
  toolRef,
  children,
  subLabel,
}) => {
  return (
    <div className={headerClasses.assessmentToolCardClasses} ref={toolRef}>
      {showHeader && !_.isEmpty(assessmentToolText) && (
        <div className={headerClasses.assessmentToolHeaderClasses}>
          {showToolIcon && toolIcon
            ? React.cloneElement(toolIcon, {
                style: styles.headerIconStyle,
              })
            : null}
          <div className={headerClasses.headerTitleClasses}>
            {assessmentToolText}
            {subLabel ? subLabel : null}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

// Render assessment tool based on type
const AssessmentToolViewerV2 = React.memo(props => {
  const {
    mediaDataAttachment, // object from theatreMode
    assessmentId,
    assessmentToolType,
    assessmentTool, // object from query response ( having row, cols details )
    assessmentToolsEvaluationDetails,
    t,
    scoreValue = null,
    maxScore = 0,
    assessmentToolsRefs,
    userType,
    handleAssessmentToolResponseInCache,
    isSharedWithStudent,
    showMediaInModal: useRevampedHeader,
    showAllTools,
  } = props;

  const {
    assessmentFields,
    unitPlanFields,
    field_list,
    isLearningGoalsEvaluationEnabled,
  } = assessmentToolsEvaluationDetails;

  // For assessment tools configurations
  const { assessmentToolText } = getAssessmentToolText({
    t,
    assessmentToolType,
    assessmentTool,
  });

  const isEvaluatedByStudent = _.get(
    mediaDataAttachment,
    "isStudentEvaluationEnable"
  );

  const isTeacherEvaluationEnable = _.get(
    mediaDataAttachment,
    "isTeacherEvaluationEnable"
  );

  const showToolWithoutStudentEvaluation =
    showAllTools || isSharedWithStudent || isEvaluatedByStudent;

  const currentToolEvaluationResponse = !_.isUndefined(
    _.get(mediaDataAttachment, "assessmentToolType")
  )
    ? _.get(
        _.find(
          _.get(
            assessmentToolsEvaluationDetails,
            "toolEvaluationResponses",
            []
          ),
          ({ toolId }) =>
            toolId == _.get(assessmentTool, "assessmentToolItem.id")
        ),
        "toolResponses",
        {}
      )
    : [];

  const commentMessage = _.get(
    assessmentToolsEvaluationDetails,
    "commentMessage"
  );

  const handleToolRef = useCallback(
    ref => {
      if (assessmentToolsRefs?.current) {
        assessmentToolsRefs.current[assessmentId] = ref;
      }
    },
    [assessmentId, assessmentToolsRefs]
  );

  const debouncedHandleAssessmentToolResponseInCache = useMemo(
    () => _.debounce(handleAssessmentToolResponseInCache, 300),
    []
  );

  const updateEvaluationCell = ({
    rowId,
    columnId,
    subjectiveResponse,
    type,
    assessmentToolId,
    isDebounce = false,
  }) => {
    const {
      updateField,
      handleAssessmentToolResponseInCache,
      userType,
    } = props;
    const { parentId, parentType, extraParams, submissionId } = _.get(
      props,
      "assessmentToolsEvaluationDetails",
      {}
    );

    // parent is not allowed to update rubric cell
    if (userType === "parent") {
      return;
    }

    let params = {
      parentId,
      parentType,
      rowId,
      type,
      columnId,
      subjectiveResponse,
      ..._.pick(extraParams, ["assignmentId", "sourceFrom", "courseId"]),
      submissionId,
    };

    if (assessmentToolId) {
      params = {
        ...params,
        assessmentToolId,
        isToolResponsesV2: true,
      };
    }

    if (isDebounce) {
      debouncedHandleAssessmentToolResponseInCache(params);
    } else {
      handleAssessmentToolResponseInCache(params);
    }

    updateField({
      key: `${rowId}_${columnId}`,
      params,
      type: "EVALUATION_TOOL",
      isDebounce: type == "SINGLE_POINT_RUBRIC",
    });
  };

  const canCurrentUserEvaluate = () => {
    const userLevelPermission =
      userType == "staff"
        ? isTeacherEvaluationEnable
        : userType == "student"
        ? isEvaluatedByStudent
        : false;
    return (
      userLevelPermission &&
      _.get(assessmentToolsEvaluationDetails, "isEvaluationEnable", false)
    );
  };

  const assessmentToolStyles = canCurrentUserEvaluate()
    ? getAssessmentToolSelfEvaluationModeStyles({
        rubricCellMinWidth: "180px",
        rubricCellMaxWidth: "192px",
      })
    : getAssessmentToolRevampedViewModeStyles({
        rubricCellMinWidth: "180px",
        rubricCellMaxWidth: "200px",
      });

  if (!showToolWithoutStudentEvaluation) {
    return null;
  }

  const toolIcon = _.get(
    ASSESSMENT_TOOL_ICON_MAPPING,
    assessmentToolType,
    null
  );

  const assessmentToolCardClasses = classNames({
    [classes.assessmentToolCardV2]: true,
    [classes.assessmentToolCardV3]: useRevampedHeader,
  });

  const assessmentToolHeaderClasses = classNames({
    [classes.assessmentToolHeader]: true,
    [classes.assessmentToolHeaderV2]: useRevampedHeader,
  });

  const headerTitleClasses = classNames({
    "text-label": useRevampedHeader,
  });

  const headerClasses = {
    assessmentToolCardClasses,
    assessmentToolHeaderClasses,
    headerTitleClasses,
  };

  switch (assessmentToolType) {
    case "RUBRIC": {
      const evaluationProps = {
        isEvaluatedByStudent,
        ...assessmentToolsEvaluationDetails,
        isEvaluation: canCurrentUserEvaluate(),
        toolEvaluationResponses: currentToolEvaluationResponse,
        updateEvaluationCell: params =>
          updateEvaluationCell({
            ...params,
            assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
            type: "RUBRIC",
          }),
        isSharedWithStudent,
        showRevampedEvaluationUI: true,
      };

      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <RubricTable
            mode={"view"}
            rubric={_.get(assessmentTool, "assessmentToolItem")}
            firstColumnHeaderText={t("common:criteria")}
            {...assessmentToolStyles.rubricStyles}
            {...evaluationProps}
            showOnlyDefaultCursor={true}
          />
        </AssessmentToolWrapper>
      );
    }

    case "CHECKLIST": {
      const evaluationProps = {
        isEvaluatedByStudent,
        ...assessmentToolsEvaluationDetails,
        isEvaluation: canCurrentUserEvaluate(),
        toolEvaluationResponses: currentToolEvaluationResponse,
        updateEvaluationCell: params =>
          updateEvaluationCell({
            ...params,
            assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
            type: "CHECKLIST",
          }),
        isSharedWithStudent,
        showRevampedEvaluationUI: true,
      };

      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <ChecklistTable
            mode={"view"}
            checklist={_.get(assessmentTool, "assessmentToolItem")}
            {...assessmentToolStyles.checklistStyles}
            {...evaluationProps}
            showOnlyDefaultCursor={true}
          />
        </AssessmentToolWrapper>
      );
    }
    case "SINGLE_POINT_RUBRIC": {
      const evaluationProps = {
        isEvaluatedByStudent,
        ...assessmentToolsEvaluationDetails,
        toolEvaluationResponses: currentToolEvaluationResponse,
        updateEvaluationCell: params =>
          updateEvaluationCell({
            ...params,
            assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
            type: "SINGLE_POINT_RUBRIC",
          }),
        isEvaluation: true,
        isEvaluationEnable: canCurrentUserEvaluate(),
        showRevampedEvaluationUI: true,
      };

      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <SinglePointRubricTable
            mode={"view"}
            rubric={_.get(assessmentTool, "assessmentToolItem")}
            {...assessmentToolStyles.singlePointRubricStyles}
            {...evaluationProps}
          />
        </AssessmentToolWrapper>
      );
    }

    case "COMMENT":
      return (
        <div className={assessmentToolCardClasses} ref={handleToolRef}>
          <div>
            <div className={assessmentToolHeaderClasses}>
              {useRevampedHeader
                ? React.cloneElement(toolIcon, {
                    style: styles.headerIconStyle,
                  })
                : null}
              <h6 className={headerTitleClasses}>
                {`${assessmentToolText}: `}
                {!_.isEmpty(commentMessage) ? (
                  <span className={classes.commentText}>{commentMessage}</span>
                ) : (
                  <span
                    className={classNames("text-body", classes.placeholderText)}
                  >
                    {t("classRoom:teacher_comment_placeholder")}
                  </span>
                )}
              </h6>
            </div>
          </div>
        </div>
      );

    case "MYP_OBJECTIVE_RUBRIC":
    case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC": {
      const evaluationProps = {
        ...assessmentToolsEvaluationDetails,
        isEvaluated: _.get(
          assessmentToolsEvaluationDetails,
          "isEvaluated",
          true
        ),
        isEvaluatedByStudent,
        toolEvaluationResponses: currentToolEvaluationResponse,
        updateEvaluationCell: params =>
          updateEvaluationCell({
            ...params,
            assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
            type: assessmentToolType,
          }),
        isEvaluationEnable: canCurrentUserEvaluate(),
        showRevampedEvaluationUI: true,
      };

      const measureAssessingValue = _.includes(
        ["MYP_OBJECTIVE_RUBRIC", "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC"],
        assessmentToolType
      )
        ? assessmentToolType
        : undefined;

      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <MYPObjectiveRubric
            mode={"view"}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            showObjectiveNameInTable={!canCurrentUserEvaluate()}
            options={getMYPObjectiveRubricOptions({
              unitPlanFields,
              assessmentFields,
              field_list,
              measureAssessingValue,
            })}
            {...assessmentToolStyles.mypObjectiveRubricStyles}
            {...evaluationProps}
            showOnlyDefaultCursor={true}
          />
        </AssessmentToolWrapper>
      );
    }

    case "DP_ASSESSMENT_RUBRIC": {
      const evaluationProps = {
        isEvaluationEnable: canCurrentUserEvaluate(),
        ...assessmentToolsEvaluationDetails,
      };
      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <CriteriaRubricAssessmentWrapper
            criteria={_.get(assessmentTool, "assessmentToolItem.criteria")}
            mode={"view"}
            customHeaderLabelClass={"text-heading-6"}
            customSelectedRatingElement={classes.iconSelected}
            customRatingElementClass={classes.customRatingElementClass}
            marksLabelLocale={"classRoom:total_marks_with_score"}
            showRevampedUI={true}
            {...evaluationProps}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
          />
        </AssessmentToolWrapper>
      );
    }

    case "LEARNING_GOALS": {
      const {
        assessmentFields,
        unitPlanFields,
        field_list,
      } = assessmentToolsEvaluationDetails;
      const fieldObj = _.get(field_list, "addLearningGoals", {}) || {};
      const options = _.get(fieldObj, "config.options", []);
      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={t("common:learning_goals")}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
          subLabel={
            !isLearningGoalsEvaluationEnabled ? (
              <span className={classNames("text-body", classes.subLabel)}>
                {` (${t("classRoom:no_evaluation_enabled")})`}
              </span>
            ) : null
          }
        >
          <AddLearningGoals
            mode={"view"}
            assessmentId={_.get(
              assessmentToolsEvaluationDetails,
              "assessmentId"
            )}
            assessmentFields={assessmentFields}
            unitPlanFields={unitPlanFields}
            field_list={field_list}
            defaultOptions={options}
            showExpandedLearningGoals={true}
            assessmentToolsEvaluationDetails={assessmentToolsEvaluationDetails}
            showLearningGoalsRating={
              isLearningGoalsEvaluationEnabled && isSharedWithStudent
            }
          />
        </AssessmentToolWrapper>
      );
    }

    case "SCORE":
      return (
        <div
          className={classes.assessmentToolCardV2}
          style={{ width: "100%" }}
          ref={handleToolRef}
        >
          <div>
            <h6>{`${assessmentToolText}: `}</h6>
            <span className={"text-body-l"}>{`${
              !_.isEmpty(scoreValue) ? scoreValue : "-"
            }/${maxScore}`}</span>
          </div>
        </div>
      );

    case ASSESSMENT_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          showHeader={false}
          toolRef={handleToolRef}
        >
          <AssessmentIBScaleTool
            {...assessmentToolsEvaluationDetails}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluationEnable={canCurrentUserEvaluate()}
            toolStyles={styles.toolStyles}
            customSelectedRatingElement={classes.iconSelected}
            customRatingElementClass={classes.customRatingElementClass}
          />
        </AssessmentToolWrapper>
      );

    case "ANALYSIS": {
      const { assessmentTools } = props;
      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <AIAnalysisScale
            {...props}
            name={props.fieldKey}
            assessmentTools={_.map(assessmentTools, assessmentTool => ({
              node: assessmentTool,
            }))}
            mode="view"
          />
        </AssessmentToolWrapper>
      );
    }

    case SCORE_BASED_RUBRIC:
    case STANDARD_BASED_RUBRIC: {
      const _updateEvaluationCell = params => {
        updateEvaluationCell({
          ...params,
          assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
          type: assessmentToolType,
        });
      };

      return (
        <AssessmentToolWrapper
          toolIcon={toolIcon}
          assessmentToolText={assessmentToolText}
          showToolIcon={useRevampedHeader}
          headerClasses={headerClasses}
          toolRef={handleToolRef}
        >
          <StandardRubric
            mode={"view"}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluatedByStudent={isEvaluatedByStudent}
            {...assessmentToolsEvaluationDetails}
            isEvaluationEnable={canCurrentUserEvaluate()}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={_updateEvaluationCell}
            type={assessmentToolType}
            {...assessmentToolStyles.standardRubricStyles}
          />
        </AssessmentToolWrapper>
      );
    }

    case GRADE_SCALE_TOOL: {
      const displayName = _.get(
        assessmentTool,
        "assessmentToolItem.gradeScale.displayName"
      );

      const { ibPYPElementRatings } = assessmentToolsEvaluationDetails || {};

      const gradeScaleToolRating = _.find(ibPYPElementRatings, {
        ibPYPElementType: GRADE_SCALE_TOOL,
      });

      const value = _.get(
        gradeScaleToolRating,
        "academicCriteriaValue.abbreviation"
      );

      return (
        <div className={assessmentToolCardClasses} ref={handleToolRef}>
          <div className={classes.gradeScaleContainer}>
            <div className={assessmentToolHeaderClasses}>
              {useRevampedHeader
                ? React.cloneElement(toolIcon, {
                    style: styles.headerIconStyle,
                  })
                : null}
              <h6 className={headerTitleClasses}>
                {`${displayName}: `}
                {!_.isEmpty(value) ? (
                  <span className={classes.commentText}>{value}</span>
                ) : (
                  "-"
                )}
              </h6>
            </div>

            {!_.isEmpty(value) ? (
              <AssessmentGradeScaleTool
                mode={"view"}
                assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
                {...assessmentToolsEvaluationDetails}
                {...assessmentToolStyles.assessmentGradeScaleToolStyles}
              />
            ) : null}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
});

const mapStateToProps = (state, ownProps) => {
  const { assessmentTools } = ownProps;

  const { isScoreEnabled, maxScore } = getScoreDetailsMemoize({
    assessmentTools: {
      edges: _.map(assessmentTools, tool => ({ node: tool })),
    },
  });

  const isScoringEnabled =
    _.get(
      ownProps?.assessmentToolsEvaluationDetails,
      "isTeacherEvaluationEnabled"
    ) &&
    isScoreEnabled &&
    maxScore;

  let scoreValue;
  const ibPYPElementRatings = _.get(
    ownProps?.assessmentToolsEvaluationDetails,
    "ibPYPElementRatings"
  );

  const isSharedWithStudent = _.get(
    ownProps,
    "assessmentToolsEvaluationDetails.isSharedWithStudent",
    false
  );

  if (isScoringEnabled) {
    scoreValue =
      getUserType(state) == "staff" || isSharedWithStudent
        ? getScoreValueElementRatings({
            ibPYPElementRatings,
          })
        : null;
  }

  const visibleIbPYPElementRatings =
    getUserType(state) == "staff" || isSharedWithStudent
      ? ibPYPElementRatings
      : [];

  const updatedAssessmentToolsEvaluationDetails = {
    ..._.get(ownProps, "assessmentToolsEvaluationDetails", {}),
    ibPYPElementRatings: visibleIbPYPElementRatings,
  };

  return {
    isData: true,
    isLoading: false,
    scoreValue,
    maxScore,
    userType: _.get(state, "login.userInfo.user_type", ""),
    parentId: _.get(
      ownProps,
      "assessmentToolsEvaluationDetails.parentId",
      null
    ),
    parentType: _.get(
      ownProps,
      "assessmentToolsEvaluationDetails.parentType",
      null
    ),
    assessmentToolsEvaluationDetails: updatedAssessmentToolsEvaluationDetails,
    isSharedWithStudent,
  };
};

const mapActionCreators = {
  updateField,
  handleAssessmentToolResponseInCache,
};
const GrouppedAssessmentToolsViewerV2 = props => {
  const assessmentToolsRefs = useRef([]);
  const {
    assessmentToolsData = [],
    isWorksheet,
    assessmentToolsEvaluationDetails: {
      isTeacherEvaluationEnabled,
      isStudentEvaluationEnabled,
    } = {},
    isSharedWithStudent,
    showMediaInModal = false,
  } = props;

  const { t } = useI18n();

  useEffect(() => {
    const mediaAttachments = _.get(props, "mediaAttachments.attachments", []);
    const selectedAssessmentTool = _.get(props, [
      "assessmentTools",
      props?.activeIndex - _.size(mediaAttachments, 0),
    ]);
    const ref = assessmentToolsRefs.current[selectedAssessmentTool?.id];
    if (!_.isNil(ref) && !_.isEmpty(ref)) {
      ref.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [props?.activeIndex]);

  const areSomeToolsWithEvaluationIndicator = _.some(
    _.get(props, "assessmentTools", []),
    assessmentTool => {
      const assessmentToolType = assessmentTool?.assessmentToolType;
      const { showEvaluationIndicator = false } =
        _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }) || {};
      return showEvaluationIndicator;
    }
  );

  const groupContainerClasses = classNames({
    [classes.groupContainerV2]: true,
    [classes.groupContainerV3]: showMediaInModal,
  });

  const evaluationStatusClasses = classNames({
    [classes.evaluationStatus]: true,
    [classes.evaluationStatusInModal]: showMediaInModal,
  });

  return (
    <div className={groupContainerClasses}>
      {(isTeacherEvaluationEnabled || isStudentEvaluationEnabled) &&
      isSharedWithStudent &&
      areSomeToolsWithEvaluationIndicator ? (
        <div className={evaluationStatusClasses}>
          {isTeacherEvaluationEnabled ? (
            <div className={classes.evaluationStatusItem}>
              <div
                className={classNames(
                  classes.evaluationStatusDotIcon,
                  classes.iconSelected
                )}
              />
              <div className="text-body">{t("common:teacher_evaluation")}</div>
            </div>
          ) : null}
          {isStudentEvaluationEnabled ? (
            <div className={classes.evaluationStatusItem}>
              <div className={classes.evaluationStatusDotIcon} />
              <div className="text-body">{t("common:student_evaluation")}</div>
            </div>
          ) : null}
        </div>
      ) : null}
      {_.map(_.get(props, "assessmentTools", []), assessmentTool => {
        const assessmentToolData = _.find(
          assessmentToolsData,
          item =>
            item?.id == assessmentTool?.id &&
            item?.assessmentToolType == assessmentTool?.assessmentToolType
        );

        const assessmentId = _.get(assessmentTool, "id");
        const assessmentToolType = _.get(assessmentTool, "assessmentToolType");
        const assessmentEntityType =
          assessmentToolType != "LEARNING_GOALS"
            ? _.get(
                _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
                "entityType",
                ""
              )
            : "LEARNING_GOALS";
        const hideAssessmentToolInModal =
          isWorksheet && assessmentToolType == "SCORE" && showMediaInModal;

        return hideAssessmentToolInModal ? null : (
          <div key={assessmentId}>
            <AssessmentToolViewerV2
              {...props}
              mediaDataAttachment={assessmentTool}
              assessmentTool={assessmentToolData}
              assessmentToolsRefs={assessmentToolsRefs}
              assessmentId={assessmentId}
              assessmentEntityType={assessmentEntityType}
              assessmentToolType={assessmentToolType}
            />
          </div>
        );
      })}
    </div>
  );
};

const getPublicLinkProps = props => {
  const extraProps = { isPublicURL: false };
  if (
    _.includes(_.get(props, "ownProps.location.pathname", ""), "/public/post/")
  ) {
    extraProps.isPublicURL = true;
  }
  return extraProps;
};
export default compose(
  connect(mapStateToProps, mapActionCreators),
  withQueryParamsBasedInterface({ cb: getPublicLinkProps }),
  graphql(getAssessmentToolsDetailsByStudentAssignmentIdQuery, {
    name: "getAssessmentToolsDetailsByStudentAssignmentId",
    options: ({ parentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: parentId },
    }),
    skip: props =>
      _.get(props, "isPublicURL") ||
      _.isNil(props.parentId) ||
      _.isEmpty(props.parentId) ||
      _.isEqual(props?.parentType, QUESTION_SUBMISSION),
    props({
      getAssessmentToolsDetailsByStudentAssignmentId,
      ownProps: { isData, isLoading, parentId },
    }) {
      const queryData = getAssessmentToolsDetailsByStudentAssignmentIdFromCache(
        {
          id: parentId,
        }
      );
      const networkStatus = _.get(
        getAssessmentToolsDetailsByStudentAssignmentId,
        "networkStatus"
      );
      return {
        assessmentToolsData: _.map(
          _.get(queryData, "assignment.content.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAssessmentToolsDetailsByPostIdQuery, {
    name: "getAssessmentToolsDetailsByPostId",
    options: ({ params = {} }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: _.get(params, "postId") },
    }),
    skip: props =>
      !_.get(props, "isPublicURL") || _.isEmpty(_.get(props, "params.postId")),
    props({
      getAssessmentToolsDetailsByPostId,
      ownProps: { isData, isLoading, params },
    }) {
      const queryData = getAssessmentToolsDetailsByPostIdFromCache({
        id: _.get(params, "postId"),
      });
      const networkStatus = _.get(
        getAssessmentToolsDetailsByPostId,
        "networkStatus"
      );
      const assignmentItem = _.get(
        _.first(_.get(queryData, "items")),
        "item",
        {}
      );
      return {
        assessmentToolsData: _.map(
          _.get(assignmentItem, "assignment.content.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(assignmentItem) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAssessmentToolsByQuestionSubmissionIdQuery, {
    name: "getAssessmentToolsByQuestionSubmissionId",
    options: ({ parentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: parentId },
    }),
    skip: props =>
      _.get(props, "isPublicURL") ||
      _.isNil(props.parentId) ||
      !_.isEqual(props?.parentType, QUESTION_SUBMISSION),
    props({
      getAssessmentToolsDetailsByStudentAssignmentId,
      ownProps: { isData, isLoading, parentId },
    }) {
      const queryData = getAssessmentToolsByQuestionSubmissionIdFromCache({
        id: parentId,
      });
      const networkStatus = _.get(
        getAssessmentToolsDetailsByStudentAssignmentId,
        "networkStatus"
      );
      return {
        assessmentToolsData: _.map(
          _.get(queryData, "question.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(GrouppedAssessmentToolsViewerV2);
AssessmentToolViewerV2.displayName = "AssessmentToolViewerV2";
